import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import { useAmplitude } from '@risksmart-app/components/src/segment';
import { getColorStyles } from '@risksmart-app/components/src/utils/colours';
import { type Ref, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDashboardWidgetSettings } from 'src/context/useDashboardWidgetSettings';
import { conformanceIndicatorRating } from 'src/pages/indicators/calculateConformanceRating';
import type { IndicatorTableFields } from 'src/pages/indicators/types';

import {
  buildIndicatorReferenceLines,
  type IndicatorReferenceValues,
} from '../../../indicators/indicatorReferenceLines';
import { useGetWidgetData } from '../../gigawidget/hooks/useGetWidgetData';
import type { WidgetRef } from '../../types';
import { dataSources } from '../../universal-widget/data-sources';
import { useDashboardStore } from '../../useDashboardStore';
import { useWidgetSettingsHandler } from '../../widget-context/useWidgetSettingsHandler';
import { convertDateRangeValues } from '../filterHelpers';
import LineChartWidget from '../line-chart-widget/LineChartWidget';
import { IndicatorResultsOverTimeSettingsModal } from './IndicatorResultsOverTimeSettingsModal';
import type { IndicatorResultsOverTimeSettings } from './indicatorResultsOverTimeSettingsSchema';
import {
  hasAnyReferenceValue,
  resolveNextShowTolerances,
  resolveToleranceToggleState,
} from './toleranceToggleState';
import { sortByDateX } from './utils';

export const IndicatorResultsOverTime = (
  _props: unknown,
  ref: Ref<WidgetRef>
) => {
  const [showModal, setShowModal] = useState(false);
  const [settings, setSettings] =
    useDashboardWidgetSettings<IndicatorResultsOverTimeSettings>();
  const { t } = useTranslation(['common'], {
    keyPrefix: 'dashboard.widgets.indicatorResultsOverTime',
  });
  const amplitude = useAmplitude();
  const tolerancesEnabled = useBooleanFlagValue(
    'indicator_widget_tolerances',
    false
  );

  useWidgetSettingsHandler(ref, () => setShowModal(true));

  const { filters } = useDashboardStore();
  const { startDate, endDate } = convertDateRangeValues(filters.dateRange);

  const dataSource = dataSources.indicator;
  const {
    tableProps: { allItems },
    loading,
  } = useGetWidgetData({
    dataSource,
    propertyFilterQuery: settings?.filtering,
    variables:
      filters.dateRange && startDate && endDate
        ? {
            resultsWhere: {
              // This chart only makes sense with numeric results
              TargetValueNum: { _is_null: false },
              ResultDate: {
                _gte: startDate.toISOString(),
                _lte: endDate.toISOString(),
              },
            },
          }
        : {},
  });

  const { series, validIndicators } = useIndicatorResultsOverTime(allItems);

  const {
    getByValue: statusGetByValue,
    getColorClass,
    getLabel,
  } = useRating('indicator_conformance_status');

  // The tolerance/appetite toggle only applies to a widget showing exactly one
  // indicator that actually has reference values defined.
  const singleIndicator =
    validIndicators.length === 1 ? validIndicators[0] : undefined;
  const referenceValues: IndicatorReferenceValues = {
    upperTolerance: singleIndicator?.UpperToleranceNum,
    lowerTolerance: singleIndicator?.LowerToleranceNum,
    upperAppetite: singleIndicator?.UpperAppetiteNum,
    lowerAppetite: singleIndicator?.LowerAppetiteNum,
  };

  const { canToggle: canToggleTolerances, showLines } =
    resolveToleranceToggleState({
      tolerancesEnabled,
      isSingleIndicator: singleIndicator != null,
      hasReferenceValues: hasAnyReferenceValue(referenceValues),
      storedShowTolerances: settings?.showTolerances,
    });

  const referenceLines = showLines
    ? buildIndicatorReferenceLines(referenceValues, { getColorClass, getLabel })
    : undefined;

  const getResultConformanceRating = useCallback(
    (seriesIndex: number, y: number | undefined) => {
      const indicator = validIndicators[seriesIndex];
      const rating = conformanceIndicatorRating(indicator, {
        TargetValueNum: y,
      });

      return statusGetByValue(rating);
    },
    [statusGetByValue, validIndicators]
  );

  const handleSave = async (data: IndicatorResultsOverTimeSettings) => {
    const previous = settings?.showTolerances === true;
    const nextShowTolerances = resolveNextShowTolerances({
      canToggle: canToggleTolerances,
      formShowTolerances: data.showTolerances,
      storedShowTolerances: settings?.showTolerances,
    });

    // Only fire when the toggle is offered and actually changed. An untouched
    // save produces nextShowTolerances === previous (no event) because the
    // modal seeds showTolerances from the persisted settings — see the `values`
    // default in IndicatorResultsOverTimeSettingsModal.
    if (canToggleTolerances && nextShowTolerances !== previous) {
      amplitude.logEvent('Indicator Widget Tolerances Toggled', {
        enabled: nextShowTolerances,
      });
    }

    setSettings({ ...data, showTolerances: nextShowTolerances });
  };

  return (
    <>
      {showModal && (
        <IndicatorResultsOverTimeSettingsModal
          onDismiss={() => setShowModal(false)}
          onSave={handleSave}
          dataSource={dataSource}
          showTolerancesToggle={canToggleTolerances}
        />
      )}
      <LineChartWidget
        series={series}
        isLoading={loading}
        xTitle={t('xTitle')}
        yTitle={t('yTitle')}
        referenceLines={referenceLines}
        tooltipYValueFormatter={(y, seriesIndex) => {
          const statusLabel = getResultConformanceRating(seriesIndex, y)?.label;

          return statusLabel ? `${String(y)} (${statusLabel})` : String(y);
        }}
        pointColorFormatter={(y, seriesIndex) => {
          return getColorStyles(
            getResultConformanceRating(seriesIndex, y)?.color
          )?.backgroundColor;
        }}
      />
    </>
  );
};

const useIndicatorResultsOverTime = (
  data: readonly IndicatorTableFields[] | undefined
) => {
  const validIndicators =
    data?.filter(
      (indicator) =>
        indicator.orderedResults.length > 0 &&
        indicator.orderedResults.some((r) => r.TargetValueNum != null)
    ) ?? [];

  const series = validIndicators.map((indicator) => ({
    name: indicator.Title,
    data: indicator.orderedResults
      .filter((result) => result.TargetValueNum != null)
      .map((result) => ({
        x: result.ResultDate,
        y: result.TargetValueNum ?? 0,
      }))
      .sort(sortByDateX),
  }));

  return { series, validIndicators };
};
